import React, { memo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import makeStyles from '@mui/styles/makeStyles';
import dayjs from "util/dayjs-init.js";

import { CARGO_STYLES } from "../../../constants/cargo/styles";
import MaximizeIcon from "../../trendPage/icon/maximizeIcon.jsx";
import MinimizeIcon from "../../trendPage/icon/minimizeIcon.jsx";

import { BarChart } from "../../chart/barChart";
import { CARGO_CHARTS } from "../../../constants/cargo/constants";
import { CsvDownload } from "../../trendPage/commonComponent/csvDownload.jsx";
import { DateTimeDisplay } from "../../dateTimePicker/dateTimeDisplay.jsx";
import { useDispatch, useSelector } from "react-redux";
import { setNewShorthand } from "../../../model/slice/dateTimeShorthandSlice";
import { fetchTemperatureTrendData } from "../useTrendCargoData";
import { NoData } from "../../offlineComponent/noData";
import localizationKeys from "../../../i18n/localizationKeys.js";
import { useTranslation } from "react-i18next";
import ResetIcon from "../../../../assets/images/alarmIcon/reset-icon.svg";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType.js";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import { CargoDateTimePicker } from "../../dateTimePicker/cargoDateTimePicker.jsx";
import { HANDLING_TEMP_TAGS, TRANSPORT_TEMP_TAGS } from "../../../constants/cargo/charts.js";
import { CARGO_PAGES } from "../../../constants/cargo/pages.js";

const useStyles = makeStyles(() => ({
  container: {
    ...CARGO_STYLES.paper,
    paddingBottom: "20px",
  },
  titleContainer: {
    padding: "10px 24px",
    height: "43px",
    borderBottom: "solid 1.3px #222831",
    display: "flex",
    alignItems: "center",
  },
  title: {
    width: "60%",
  },
  picker: {
    width: "40%",
    textAlign: "right",
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    "& div": {
      marginLeft: "12px",
      width: "25px",
      height: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "end",
      "& img": {
        cursor: "pointer",
      },
    },
  },
  contentContainer: {
    width: "100%",
    height: "calc(100% - 43px)",
    padding: "5px 0 0 24px",
  },
  dateTimeDisplay: {
    display: 'flex',
    margin: "0 20px 0 auto",
  },
  goLatestButton: {
    display: "flex",
    padding: "5px 7px 5px 7px",
    border: 'solid 1px white',
    marginRight: "50px",
    cursor: "pointer",
    alignItems: "center",
    fontSize: "11px",
  }
}));

export const Temperature = memo((props) => {
  const { vesselId, isMaximized, setMaximize, latestData, page } = props;
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const [duration, setDuration] = useState(null);
  const [goLatest, setGoLatest] = useState(true);
  const [isLatest, setIsLatest] = useState(false);
  const [latestDateTime, setLatestDateTime] = useState();
  const timezone = useSelector(selectedTimezone);

  const tempData = fetchTemperatureTrendData({
    vesselId,
    duration,
    goLatest,
    setGoLatest,
    isLatest,
    latestData,
  });
  const [selectedDate, setSelectedDate] = useState(null);

  const formatDataForCSV = (data) => {
    const processedValue = data.reduce((previous, current) => {
      return Object.assign(previous, {[current.tag]: current.value});
    }, {});
    const returnValue = Object.assign(processedValue, {dateTime: selectedDate})
    return [returnValue];
  }
  
  const filterTempDataByPage = (data, page) => {
    if(data.length === 0) return [];
    if(page === CARGO_PAGES.TRANSPORT) {
      return data.filter((item) => TRANSPORT_TEMP_TAGS.some((e) => e.tag === item.tag));
    }else if(page === CARGO_PAGES.HANDLING){
      return data.filter((item) => HANDLING_TEMP_TAGS.some((e) => e.tag === item.tag));
    }else{
      return [];
    }
  }
  const tempChartData = filterTempDataByPage(tempData.cargoTempData, page);
  const backToLatest = () => {
    if(isLatest || tempData.isLoading) return;
    setGoLatest(true);
  };

  useEffect(() => {
    setLatestDateTime(latestData[0]?.dateTime ? dayjs(latestData[0]?.dateTime) : null);
  }, [latestData]);

  useEffect(() => {
    if(goLatest || isLatest){
      setSelectedDate(latestDateTime ?? new Date(dayjs.utc().subtract(1, 'm')));
    }
  }, [latestDateTime, goLatest])

  useEffect(() => {
    setIsLatest(selectedDate === latestDateTime);
  }, [latestDateTime, selectedDate]);

  useEffect(() => {
    if(!selectedDate) return;
    const startDate = dayjs(selectedDate);
    const endDate = dayjs(selectedDate).endOf("m");

    setDuration({ startDate, endDate });
  }, [selectedDate]);

  const updateSelectedDate = (newDateTime) => {
    if (selectedDate !== newDateTime && newDateTime !== "") {
      setSelectedDate(newDateTime);
    }
  }

  const submitDateTimeChangeForShorthand = (newDateTime) => {
    updateSelectedDate(newDateTime);
  };

  const submitDateTimeChange = (newDateTime, newShort, newLong) => {
    updateSelectedDate(newDateTime);
    dispatch(setNewShorthand({ short: newShort, long: newLong }));
  };

  return (
    <Grid container className={styles.container}>
      <Grid item size={{xs:12}} className={styles.titleContainer}>
        <div className={styles.title}>{t(localizationKeys.CargoTemperature)}</div>
        <div className={styles.picker}>
          <div>
            <CargoDateTimePicker date={selectedDate}
              submitDateTimeChange={submitDateTimeChange}
              isMaximized={isMaximized}
              latestDateTime={latestDateTime}/>
          </div>
          <div>
            <CsvDownload
              vesselId={vesselId}
              graphData={formatDataForCSV(tempChartData)}
              graphType={GRAPH_TYPE.CARGO}
              selectedChart={"temperature"}
              isCargo={true}
            />
          </div>
          <div onClick={() => setMaximize(CARGO_CHARTS.TEMPERATURE)}>
            {isMaximized
              ? <MinimizeIcon width={20} height={20} disabled={tempData?.isLoading} />
              : <MaximizeIcon width={20} height={20} disabled={tempData?.isLoading} />
            }
          </div>
        </div>
      </Grid>
      <Grid className={styles.dateTimeDisplay}>
        <div className={styles.goLatestButton} 
          style={{opacity: isLatest || tempData.isLoading ? 0.5 : 1.0}} 
          onClick={() => {backToLatest()}}
        >
          <img src={ResetIcon} width={20} height={20} style={{marginRight: "2px"}}/>
          <span>{t(localizationKeys.BackToLatest)}</span>
        </div>
        <DateTimeDisplay
          date={selectedDate}
          submitDateTimeChange={submitDateTimeChangeForShorthand}
          latestDateTime={latestDateTime}
          timezone={timezone}
        />
      </Grid>
      <Grid container className={styles.contentContainer}>
        {
          tempData.isLoading
            ? <div style={{margin: 'auto'}}><CircularProgress /></div>
            : tempChartData?.length > 0 ? (
              <BarChart data={tempChartData} />
              ) : (
              <NoData />
          )
        }
      </Grid>
    </Grid>
  );
});

Temperature.propTypes = {
  vesselId: PropTypes.string,
  isMaximized: PropTypes.bool,
  setMaximize: PropTypes.func,
  latestDateTime: PropTypes.string,
  latestData: PropTypes.array,
  page: PropTypes.string
};
